import { Inject } from '@odinjs/odin';
import moment from 'moment';

import { Anexo, catchRenderException, Endpoint, inISOToDatepicker, inISOToTimestamp, outDatepickerToISO, outTimestampToISO, Retorno, setRequiredField } from '@philips-emr/hpms-stipulator-commons';
import { CheckboxAPI, DateFieldAPI, FetchAPI, FieldAPI, FieldListAPI, FieldListValue, FieldsetAPI, FormAPI, InputAPI, LocatorAPI, MapperAPI, PanelAPI, RadioGroupAPI, RadioGroupItem, StampAPI, StampIcon, TextAPI, ToastNotificationAPI, WizardAPI } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import { ValidateEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService } from '@philips-emr/tws-core/lib/services';
import { RequestResponse } from '@philips-emr/tws-core/lib/services/http-service';
import { isEmptyArrayOrObject, isEmptyObject, isEmptyOrBlankString } from '@philips-emr/tws-utils';

import { EnderecoData } from '../../interfaces/logradouro';
import BaseController from '../BaseController';

import './exclusao-beneficiario-controller.scss';

export enum IeFormaEnvio {
  EMAIL = '1',
  CARTA = '2',
}

export enum IeStatusTrabalhista {
  S = 'S',
  N = 'N',
}

export enum IeStatus {
  S = 'S',
  X = 'X',
}

export enum IeObrigatoriedadeTelefone {
  A = 'A',
  C = 'C',
  T = 'T'
}

interface RetornoData {
  nrProtocoloAtend: string;
}

interface DataRequest {
  nrSeqSegurado: number;
  nrSeqSolicRescisao: number;
}

export interface MotivoExclusao {
  nrSeqCausaRescisao: number;
  dsCausaRescisao: string;
  dsMensagemWeb: string;
  ieObito: IeStatus;
  ieSituacaoTrabalhista: IeStatus;
  ieObrigatoriedadeTelefone: IeObrigatoriedadeTelefone;
  ieCarta: IeStatus;
  ieEmail: IeStatus;
  ieSuspensaoAtendimento: string;
  ieIniciativaBeneficiario: string;
}

export interface BeneficiarioSelecionado {
  nrSeqSegurado: number;
  dsTitularidade: string;
  ieSituacaoTrabalhista: IeStatusTrabalhista;
}

export interface RetornoRescisao {
  nrSeqSolicRescisao: number;
  nrSeqSegurado: number;
  protocolos: RetornoData[];
  ieLiberarRescisao: string;
}

export interface MotivoRejeicao {
  dsMotivoRejeicao: string;
  dsStatus: string;
  dsObservacaoRejeicao: string;
}

export interface RadioGroupItemData {
  key: string;
  label: string;
  value: string;
}

export default class BaseExclusaoBeneficiarioController extends BaseController {

  protected readonly identifierStepRescisao = 'wizard-step-rescisao';
  protected readonly identifierStepObito = 'wizard-step-obito';
  protected readonly identifierStepComplementar = 'wizard-step-complementar';
  protected readonly identifierStepSituacaoTrabalhista = 'wizard-step-situacao-trabalhista';
  protected readonly identifierStepContato = 'wizard-step-contato';
  protected readonly identifierStepAnexo = 'wizard-step-anexo';
  protected readonly ieFuncionalidade = 16;

  protected readonly cartaItemRadio = {
    key: 'carta',
    label: 'beneficiario.carta',
    value: IeFormaEnvio.CARTA,
  };

  protected readonly emailItemRadio = {
    key: 'email',
    label: 'stipulator.email',
    value: IeFormaEnvio.EMAIL,
  };

  protected contato: EnderecoData | undefined;
  protected shouldHiddenAnexoStep: boolean | undefined = false;
  protected shouldHiddenAnexoPromise: Promise<void>;
  protected listFieldContatos: FieldAPI[] = [];
  protected listForms: FormAPI[] = [];
  protected beneficiarioExclusao: DataRequest | undefined;
  protected isEdit: boolean = false;
  protected isFormsOnlyReadonly: boolean = false;
  protected isReenvioSolicitacao: boolean = false;
  protected anexosReenvio: Anexo[] = [];
  protected dtSolicitacaoRescisaoInput: string | undefined;

  @Inject('wizard-nova-exclusao')
  protected wizardNovaExclusao: WizardAPI;

  @Inject('radio-group-forma-contato')
  protected modoContato: RadioGroupAPI;

  @Inject('hpms-custom-field-list-beneficiarios')
  protected hpmsCustomFieldListBeneficiarios: FieldListAPI;

  @Inject('fieldset-endereco')
  protected enderecoFieldSet: FieldsetAPI;

  @Inject('fieldset-email')
  protected emailFieldSet: FieldsetAPI;

  @Inject('field-forma-contato')
  protected fieldFormaContato: FieldAPI;

  @Inject('field-email')
  protected emailField: FieldAPI;

  @Inject('field-telefone')
  protected telefoneField: FieldAPI;

  @Inject('field-celular')
  protected celularField: FieldAPI;

  @Inject('field-anexo')
  protected anexoField: FieldAPI;

  @Inject('field-tempo-contribuicao')
  protected qtTempoContribuicaoField: FieldAPI;

  @Inject('field-dt-concesao')
  protected dtConcessaoField: FieldAPI;

  @Inject('field-dt-obito')
  protected dtObitoField: FieldAPI;

  @Inject('field-cep')
  protected fieldCEP: FieldAPI;

  @Inject('field-endereco')
  protected fieldEndereco: FieldAPI;

  @Inject('field-numero')
  protected fieldNumero: FieldAPI;

  @Inject('field-complemento')
  protected fieldComplemento: FieldAPI;

  @Inject('field-bairro')
  protected fieldBairro: FieldAPI;

  @Inject('field-estado')
  protected fieldEstado: FieldAPI;

  @Inject('field-cidade')
  protected fieldCidade: FieldAPI;

  @Inject('field-certidao-obito')
  protected certidaoObito: FieldAPI;

  @Inject('field-dt-rescisao-date-field')
  protected dtRescisaoFieldDateField: FieldAPI;

  @Inject('field-dt-rescisao-input')
  protected dtRescisaoFieldInput: FieldAPI;

  @Inject('field-beneficiarios')
  protected fieldBeneficiariosLocator: FieldAPI;

  @Inject('field-toast')
  protected fieldToast: FieldAPI;

  @Inject('input-tempo-contribuicao')
  protected tempoContribuicaoInput: InputAPI;

  @Inject('date-dt-concesao')
  protected dataConcesaoDateField: DateFieldAPI;

  @Inject('form-rescisao')
  protected formRescisao: FormAPI;

  @Inject('form-situacao-trabalhista')
  protected formSituacaoTrabalhista: FormAPI;

  @Inject('form-complementar')
  protected formInfoComplementar: FormAPI;

  @Inject('form-anexo')
  protected formAnexo: FormAPI;

  @Inject('form-obito')
  protected formObito: FormAPI;

  @Inject('form-contato')
  protected formContato: FormAPI;

  @Inject('form-reenvio-solicitacao')
  protected formReenvioSolicitacao: FormAPI;

  @Inject('fetch-cidade')
  protected fetchCidade: FetchAPI;

  @Inject('locator-beneficiarios-exclusao')
  protected locatorBeneficiario: LocatorAPI;

  @Inject('mapper-anexos')
  protected mapperAnexosApi: MapperAPI;

  @Inject('panel-anexos')
  protected panelAnexo: PanelAPI;

  @Inject('stamp-situacao-lote')
  protected stampSituacao: StampAPI;

  @Inject('panel-control-card')
  protected panelControlCard: PanelAPI;

  @Inject('panel-form-reenvio-solicitacao')
  protected panelFormReenvioSolcitacao: PanelAPI;

  @Inject('text-observacao-rejeicao')
  protected observacaoRejeicaoText: TextAPI;

  @Inject('text-motivo-rejeicao')
  protected motivoRejeicaoText: TextAPI;

  @Inject('checkbox-reenvio-anexo')
  protected checkboxReenvioAnexo: CheckboxAPI;

  @Inject('toast-notification')
  protected toastFormDataLimite: ToastNotificationAPI;

  protected initProperties(): void {
    this.contato = undefined;
    this.shouldHiddenAnexoStep = false;
    this.shouldHiddenAnexoPromise = this.fetchCheckQtdRegraAnexo();
    this.listFieldContatos = [];
    this.listForms = [];
    this.beneficiarioExclusao = undefined;
    this.isEdit = false;
    this.isFormsOnlyReadonly = false;
    this.isReenvioSolicitacao = false;
    this.anexosReenvio = [];
    this.dtSolicitacaoRescisaoInput = undefined;
  }

  private fetchCheckQtdRegraAnexo(): Promise<void> {
    return HttpService.get<number>(Endpoint.GET.CHECK_QTD_REGRA_ANEXOS, { ieFuncionalidade: this.ieFuncionalidade })
      .then(({ body }) => {
        this.shouldHiddenAnexoStep = !(body && body > 0);
      }).catch(() => {
        this.shouldHiddenAnexoStep = false;
      });
  }

  protected saveRescisao(params: {}, body: {}): Promise<RequestResponse<Retorno<RetornoRescisao>>> {
    if (this.isEdit && !this.isReenvioSolicitacao) {
      return HttpService.put<Retorno<RetornoRescisao>>(Endpoint.PUT.EXCLUSAO_BENEFICIARIO, params, { body });
    }

    return HttpService.post<Retorno<RetornoRescisao>>(Endpoint.POST.EXCLUSAO_BENEFICIARIO, params, { body });
  }

  protected isEditStepRescisao(): void {
    if (this.isEdit) {
      return;
    }

    this.wizardNovaExclusao.editStep(this.identifierStepRescisao);
  }

  protected initConfigForms(motivoRescisao: MotivoExclusao): void {

    const { ieSolicitacaoRescisao } = this.historyService.getCurrentRouteParams();

    if (!motivoRescisao || !motivoRescisao.nrSeqCausaRescisao) {
      this.isEditStepRescisao();
      this.contato = undefined;
      return;
    }

    const hiddenSteps: string[] = [];

    if (motivoRescisao.ieObito != IeStatus.S) {
      hiddenSteps.push(this.identifierStepObito);

      catchRenderException(() => {
        this.formObito.setRecord({});
        this.formObito.setData({});
      });
    }

    if (motivoRescisao.ieSituacaoTrabalhista == IeStatus.X) {
      hiddenSteps.push(this.identifierStepSituacaoTrabalhista);

      catchRenderException(() => {
        setRequiredField(false, this.qtTempoContribuicaoField);
        setRequiredField(false, this.dtConcessaoField);

        this.formSituacaoTrabalhista.setRecord({});
        this.formSituacaoTrabalhista.setData({});
      });
    }

    if (this.shouldHiddenAnexoStep) {
      hiddenSteps.push(this.identifierStepAnexo);

      catchRenderException(() => {
        this.formAnexo.setRecord({});
        this.formAnexo.setData({});
      });
    }

    if (ieSolicitacaoRescisao === 'N') {
      hiddenSteps.push(this.identifierStepContato);

      catchRenderException(() => {
        setRequiredField(false, this.emailField);
        setRequiredField(false, this.fieldCEP);
        setRequiredField(false, this.fieldEndereco);
        setRequiredField(false, this.fieldNumero);
        setRequiredField(false, this.fieldComplemento);
        setRequiredField(false, this.fieldEstado);
        setRequiredField(false, this.fieldBairro);
        setRequiredField(false, this.fieldCidade);
        setRequiredField(false, this.telefoneField);
        setRequiredField(false, this.celularField);
        setRequiredField(false, this.fieldFormaContato);

        this.formContato.setRecord({});
        this.formContato.setData({});
      });
    }
    else {
      setRequiredField(
        [IeObrigatoriedadeTelefone.A, IeObrigatoriedadeTelefone.C].includes(motivoRescisao.ieObrigatoriedadeTelefone),
        this.celularField
      );

      setRequiredField(
        [IeObrigatoriedadeTelefone.A, IeObrigatoriedadeTelefone.T].includes(motivoRescisao.ieObrigatoriedadeTelefone),
        this.telefoneField
      );

    }

    this.wizardNovaExclusao.setHiddenSteps(hiddenSteps);

    const itens: RadioGroupItem[] = [];

    if (motivoRescisao.ieCarta != IeStatus.X) {
      itens.push(this.cartaItemRadio);
    }

    if (motivoRescisao.ieEmail != IeStatus.X) {
      itens.push(this.emailItemRadio);
    }

    const novaExclusao = this.historyService.getCurrentRoute().includes('nova-exclusao');
    
    let itemSelecionado: RadioGroupItem;

    if (ieSolicitacaoRescisao == 'S' || novaExclusao) {

      if (itens.length) {
        this.modoContato.setData(itens);
        itemSelecionado = itens[0];
      } else {
        this.modoContato.setData([this.cartaItemRadio, this.emailItemRadio]);
        itemSelecionado = this.cartaItemRadio;
      }

      if (!this.isEdit) {
        this.setDataFormContato(itemSelecionado);
        this.setIeFormaEnvioInForm(itemSelecionado);
      }

    }

    this.changeFieldDtRescisao(motivoRescisao);
  }

  protected validateMotivoRejeicao(ieSolicitacaoRescisao: string, ieStatus: number): void {
    if (!(ieSolicitacaoRescisao === 'S' && ieStatus === 5)) {
      return;
    }

    const { nrSeqContrato, nrSeqRescisao } = this.historyService.getCurrentRouteParams();

    void HttpService.get<MotivoRejeicao>(Endpoint.GET.EXCLUSAO_BENEFICIARIO_MOTIVO_REJEICAO, { nrSeqContrato, nrSeqRescisao, ieSolicitacaoRescisao }).then(({ body }) => {
      if (!body || isEmptyObject(body)) {
        return;
      }

      const { dsStatus, dsMotivoRejeicao, dsObservacaoRejeicao } = body;

      catchRenderException(() => {
        if (!this.isReenvioSolicitacao) {
          this.panelControlCard.setVisible(true);
        }

        this.motivoRejeicaoText.setValue(dsMotivoRejeicao);
        this.observacaoRejeicaoText.setValue(dsObservacaoRejeicao);

        this.stampSituacao.setColor(Color.SAFETY_RED);
        this.stampSituacao.setIcon(StampIcon.CANCELED);
        this.stampSituacao.setLabel(dsStatus);
      });
    });

  }

  protected updateFormEditingNavigation(target: string, isEditing: boolean): void {
    isEditing = this.isFormsOnlyReadonly ? false : isEditing;

    if (this.identifierStepRescisao === target) {
      this.formRescisao.setEditing(isEditing);
    } else if (this.identifierStepObito === target) {
      this.formObito.setEditing(isEditing);
    } else if (this.identifierStepComplementar === target) {
      this.formInfoComplementar.setEditing(isEditing);
    } else if (this.identifierStepSituacaoTrabalhista === target) {
      this.formSituacaoTrabalhista.setEditing(isEditing);
    } else if (this.identifierStepContato === target) {
      this.formContato.setEditing(isEditing);
    } else if (this.identifierStepAnexo === target) {
      this.formAnexo.setEditing(isEditing);
      catchRenderException(() => this.formReenvioSolicitacao.setEditing(isEditing));
    }

  }

  protected setStepDone(identifier: string, event: ValidateEvent): void {
    const form = event.target as FormAPI;

    if (this.wizardNovaExclusao.getCurrentStepIdentifier() === identifier) {
      this.wizardNovaExclusao.setStepDone(identifier, form.isValid());
    }
  }

  protected setValueFieldList(beneficiarios: FieldListValue): void {
    if (!Array.isArray(beneficiarios)) {
      return;
    }

    const ids = beneficiarios.map(beneficiario => beneficiario.nrSeqSegurado);
    this.context.set({ beneficiarios: ids });
    this.hpmsCustomFieldListBeneficiarios.setValue(beneficiarios);
    this.fetchEndereco(beneficiarios as unknown as BeneficiarioSelecionado[]);
  }

  protected fetchEndereco(beneficiarios: BeneficiarioSelecionado[]): void {
    if (this.isEdit) {
      return;
    }

    if (beneficiarios && beneficiarios.length == 1) {
      const { nrSeqContrato } = this.historyService.getCurrentRouteParams();
      void HttpService.get<EnderecoData>(Endpoint.GET.ENDERECO_BENEFICIARIO_CONTRATO, { nrSeqContrato, nrSeqBeneficiario: beneficiarios[0].nrSeqSegurado })
        .then(({ body }) => {
          this.contato = body;
          this.setDataFormContato(this.modoContato.getValue());
        });
    } else {
      this.contato = undefined;
      this.formContato.setData({});
      this.validateFormContato();
    }
  }

  protected setIeFormaEnvioInForm(ieFormaEnvio: RadioGroupItem): void {
    this.formInfoComplementar.setData({ ...this.formInfoComplementar.getRecord(), ieFormaEnvio });
  }

  protected validateFormContato(): void {
    this.listFieldContatos.forEach((field) => field.validate());
  }

  protected visibilityFieldsContato(showEmail: boolean, showEndereco: boolean): void {
    this.emailFieldSet.setVisible(showEmail);
    this.enderecoFieldSet.setVisible(showEndereco);
    this.updateFieldRequiredFormContato(showEmail, showEndereco);
  }

  protected setDataFormContato(radioItem?: RadioGroupItem): void {
    const showEmail = radioItem ?.value === IeFormaEnvio.EMAIL;
    const showEndereco = radioItem ?.value === IeFormaEnvio.CARTA;

    this.visibilityFieldsContato(showEmail, showEndereco);

    const { telefoneFormatado, celularFormatado } = this.formContato.getRecord();
    const dsUf = this.contato ?.dsUf;
    let data: Record<string, Value> = { telefoneFormatado, celularFormatado };

    if (showEmail) {
      data.dsEmail = this.contato ?.dsEmail;
    }

    if (showEndereco) {
      data = {
        ...data,
        ...(this.contato && { ...this.contato }),
        dsEmail: null,
      };

      if (dsUf) {
        catchRenderException(() => this.fetchCidade.setParameters({ dsUnidadeFederacao: dsUf }));
      }
    }

    this.formContato.setData(data);
    this.validateFormContato();
    this.formContato.validate();
  }

  protected updateFieldRequiredFormContato(emailRequired: boolean, enderecoRequired: boolean): void {
    const naoChecar = ['field-email', 'field-telefone', 'field-celular', 'field-complemento'];

    catchRenderException(() => setRequiredField(emailRequired, this.emailField));

    this.listFieldContatos.filter(field => !naoChecar.includes(field.state.alias as string))
      .forEach(field => setRequiredField(enderecoRequired, field));
  }

  protected setValidatorRequiredTempoContribuicao(isRequired: boolean): void {
    catchRenderException(() => {
      setRequiredField(isRequired, this.qtTempoContribuicaoField);
      this.qtTempoContribuicaoField.validate();
    });
  }

  protected setValidatorRequiredDtConcessaoPlano(isRequired: boolean): void {
    catchRenderException(() => {
      setRequiredField(isRequired, this.dtConcessaoField);
      this.dtConcessaoField.validate();
    });
  }

  protected habilitarCampoRescisaoDateField(): void {
    setRequiredField(false, this.dtRescisaoFieldInput);

    if (this.dtRescisaoFieldInput.isVisible()) {
      this.dtRescisaoFieldInput.setVisible(false);
    }

    this.dtRescisaoFieldDateField.setVisible(true);
    setRequiredField(true, this.dtRescisaoFieldDateField);
    this.dtRescisaoFieldDateField.validate();
    this.dtRescisaoFieldInput.validate();
  }

  protected habilitarCampoRescisaoInput(): void {
    setRequiredField(false, this.dtRescisaoFieldDateField);

    if (this.dtRescisaoFieldDateField.isVisible()) {
      this.dtRescisaoFieldDateField.setVisible(false);
    }

    this.dtRescisaoFieldInput.setVisible(true);
    setRequiredField(true, this.dtRescisaoFieldInput);
    this.dtRescisaoFieldDateField.validate();
    this.dtRescisaoFieldInput.validate();
  }

  protected getDtRescisaoFmtISO(motivoRescisao: MotivoExclusao): string | null {
    const dtSolicitacaoRescisaoDateField = this.formInfoComplementar.getRecord().dtSolicitacaoRescisaoDateField as string;
    const dtSolicitacaoRescisaoInput = this.formInfoComplementar.getRecord().dtSolicitacaoRescisaoInput as string;

    if (motivoRescisao.ieIniciativaBeneficiario === 'S') {
      return outTimestampToISO(dtSolicitacaoRescisaoInput);

    }

    if (motivoRescisao.ieIniciativaBeneficiario === 'N') {
      return outDatepickerToISO(dtSolicitacaoRescisaoDateField);
    }

    return null;
  }

  protected swapFieldsFmtDtRescisao(motivoRescisao: MotivoExclusao): void {
    if (!motivoRescisao) {
      return;
    }

    let data = {};
    let result = null;

    if (this.dtSolicitacaoRescisaoInput && !isEmptyOrBlankString(this.dtSolicitacaoRescisaoInput)) {
      if (motivoRescisao.ieIniciativaBeneficiario === 'S') {
        if (motivoRescisao.ieSuspensaoAtendimento === '1') {
          result = this.isReenvioSolicitacao ? inISOToTimestamp(new Date()) : inISOToTimestamp(this.dtSolicitacaoRescisaoInput);
        } else if (motivoRescisao.ieSuspensaoAtendimento === '2') {
          if (this.isReenvioSolicitacao) {
            result = inISOToTimestamp(new Date());
          } else {
            const date = moment(this.dtSolicitacaoRescisaoInput).toDate();
            date.setHours(23);
            date.setMinutes(59);
            date.setSeconds(59);
            result = inISOToTimestamp(date);
          }
        }

        data = { dtSolicitacaoRescisaoDateField: null, dtSolicitacaoRescisaoInput: result };
      } else if (motivoRescisao.ieIniciativaBeneficiario === 'N') {
        result = inISOToDatepicker(this.dtSolicitacaoRescisaoInput);
        data = { dtSolicitacaoRescisaoDateField: result, dtSolicitacaoRescisaoInput: null };
      }
    }

    this.formInfoComplementar.setRecord({ ...this.formInfoComplementar.getRecord(), ...data });
    this.formInfoComplementar.setData({ ...this.formInfoComplementar.getRecord(), ...data });
    this.formInfoComplementar.validate();
  }

  protected getDateFromISO(isoDate: string): string {
    return isoDate.slice(0, 10);
  }

  protected setEditFormsEditFalse(bypassFormRescisao?: boolean | false): void {
    this.listForms.filter((form) => !(bypassFormRescisao && form.state.alias === 'form-rescisao') || this.isFormsOnlyReadonly)
      .forEach((form) => form.setEditing(false));
  }

  protected changeFieldDtRescisao(motivoRescisao: MotivoExclusao): void {
    if (motivoRescisao.ieIniciativaBeneficiario === 'S') {
      this.habilitarCampoRescisaoInput();
    } else if (motivoRescisao.ieIniciativaBeneficiario === 'N') {
      this.habilitarCampoRescisaoDateField();
    }
  }

  protected setReenvioSolicitacao(): void {
    this.isReenvioSolicitacao = this.historyService.getCurrentRoute().includes('/reenviar-exclusao');
  }

  protected hiddeFormCheckbox(): void {
    catchRenderException(() => {
      if (!this.isReenvioSolicitacao && this.panelFormReenvioSolcitacao.isVisible()) {
        this.panelFormReenvioSolcitacao.setVisible(false);
      }
    });
  }

  protected setWizardReadonly(forceReadonly?: boolean): void {
    this.wizardNovaExclusao.setReadOnly(forceReadonly ?? this.isFormsOnlyReadonly);
  }

  protected fetchAnexos(nrSeqContrato: number, nrSeqRescisao: number, nrSeqSegurado: number): void {
    void HttpService.get<any>(Endpoint.GET.EXCLUSAO_BENEFICIARIO_ANEXOS, { nrSeqContrato, nrSeqRescisao, nrSeqBeneficiario: nrSeqSegurado, ieSolicitacaoRescisao: 'S' })
      .then(({ body: anexos }) => {
        if (isEmptyArrayOrObject(anexos)) {
          return;
        }

        catchRenderException(() => {
          this.formAnexo.setData({ ...this.formAnexo.getRecord(), anexos });
          this.mapperAnexosApi.setData(anexos);
        });
      });
  }

}
